import { buildTranslate } from 'locales'

import {
  createWebSocketError,
  WebSocketError,
  WebSocketErrorCodes,
} from 'services/websocket/errorHandling/WebSocketError'

const t = buildTranslate('services.websocket.error_handler')

export interface ErrorContext {
  connectionId?: string
  reconnect?: () => void
  refreshAuth?: () => void
  clearQueue?: () => void
  onError?: (error: WebSocketError) => void
}

export class WebSocketErrorHandler {
  static handle(error: unknown, context: ErrorContext = {}): WebSocketError {
    const wsError = this.normalize(error, context)

    if (context.onError) {
      context.onError(wsError)
    }

    if (wsError.recoverable) {
      this.attemptRecovery(wsError, context)
    }

    return wsError
  }

  private static normalize(
    error: unknown,
    context: ErrorContext
  ): WebSocketError {
    if (this.isWebSocketError(error)) {
      return error
    }

    if (error instanceof Error) {
      return this.fromError(error, context)
    }

    if (typeof error === 'string') {
      return createWebSocketError(
        'unknown',
        WebSocketErrorCodes.MESSAGE_PARSE_ERROR,
        error,
        { connectionId: context.connectionId }
      )
    }

    return createWebSocketError(
      'unknown',
      WebSocketErrorCodes.MESSAGE_PARSE_ERROR,
      'Unknown error occurred',
      {
        connectionId: context.connectionId,
        originalError: error,
        recoverable: false,
      }
    )
  }

  private static fromError(
    error: Error,
    context: ErrorContext
  ): WebSocketError {
    if (error.message.includes('Connection timeout')) {
      return createWebSocketError(
        'connection',
        WebSocketErrorCodes.CONNECTION_TIMEOUT,
        'Connection timed out',
        { connectionId: context.connectionId, originalError: error }
      )
    }

    if (error.message.includes('Connection failed')) {
      return createWebSocketError(
        'connection',
        WebSocketErrorCodes.CONNECTION_FAILED,
        'Failed to establish connection',
        { connectionId: context.connectionId, originalError: error }
      )
    }

    if (
      error.message.includes('authentication') ||
      error.message.includes('rejected')
    ) {
      return createWebSocketError(
        'authentication',
        WebSocketErrorCodes.AUTH_FAILED,
        'Authentication failed',
        { connectionId: context.connectionId, originalError: error }
      )
    }

    if (error.message.includes('Queue timeout')) {
      return createWebSocketError(
        'timeout',
        WebSocketErrorCodes.QUEUE_TIMEOUT,
        'Message queue timed out',
        { connectionId: context.connectionId, originalError: error }
      )
    }

    if (error.message.includes('Max retries')) {
      return createWebSocketError(
        'message',
        WebSocketErrorCodes.MAX_RETRIES_EXCEEDED,
        'Maximum retry attempts exceeded',
        {
          connectionId: context.connectionId,
          originalError: error,
          recoverable: false,
        }
      )
    }

    return createWebSocketError(
      'unknown',
      WebSocketErrorCodes.MESSAGE_PARSE_ERROR,
      error.message,
      { connectionId: context.connectionId, originalError: error }
    )
  }

  private static isWebSocketError(error: unknown): error is WebSocketError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'type' in error &&
      'code' in error &&
      'message' in error &&
      'timestamp' in error &&
      'recoverable' in error
    )
  }

  private static attemptRecovery(
    error: WebSocketError,
    context: ErrorContext
  ): void {
    switch (error.type) {
      case 'connection':
        if (context.reconnect) {
          context.reconnect()
        }
        break

      case 'authentication':
        if (context.refreshAuth) {
          context.refreshAuth()
        }
        break

      case 'timeout':
        if (context.clearQueue) {
          context.clearQueue()
        }
        break

      case 'message':
        break

      default:
        break
    }
  }

  static getMessageForUser(error: WebSocketError): string {
    switch (error.code) {
      case WebSocketErrorCodes.CONNECTION_FAILED:
        return t('connection_failed')

      case WebSocketErrorCodes.CONNECTION_TIMEOUT:
        return t('connection_timeout')

      case WebSocketErrorCodes.AUTH_FAILED:
      case WebSocketErrorCodes.AUTH_TOKEN_INVALID:
        return t('auth_failed')

      case WebSocketErrorCodes.QUEUE_TIMEOUT:
        return t('queue_timeout')

      case WebSocketErrorCodes.MAX_RETRIES_EXCEEDED:
        return t('max_retries')

      case WebSocketErrorCodes.MESSAGE_SEND_FAILED:
        return t('message_send_failed')

      case WebSocketErrorCodes.UNKNOWN_MESSAGE_TYPE:
        return t('unknown_message_type')

      default:
        return t('general_error')
    }
  }
}
